import React, { useCallback, useEffect, useState } from "react";
import { useDeal } from "../../lib/store";
import { shareApi } from "../../lib/api";
import * as m from "../../lib/model";
import { Card } from "../shared";
import { CommitBar } from "./InvestTab";
import { Timeline } from "./Timeline";
import { ROOM_SECTIONS, dealKeyFor, dealKeyAlts } from "../ShareRoomModal";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (s) => (s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const label = (id) => ROOM_SECTIONS.find((s) => s.id === id)?.label || id;

export function RoomActivityCard({ refreshKey, onShare, compact = false }) {
  const { M, dealId } = useDeal();
  const [share, setShare] = useState(null);
  const [act, setAct] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const s = await shareApi.byDeal(dealKeyFor(dealId, M), dealKeyAlts(dealId, M));
      setShare(s);
      setAct(s ? await shareApi.activity(s.id) : null);
    } catch { /* not published yet */ } finally { setBusy(false); }
  }, [dealId, M.dealName, M.dealKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!share) return (
    <Card title="Deal Room" testid="room-activity-empty">
      <div className="bullet">No investor deal room published for this deal yet.</div>
      <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={onShare} data-testid="room-activity-share">Set up the Deal Room</button>
    </Card>
  );

  const ask = share.equityAsk > 0 ? share.equityAsk : m.lpAskOf(M);
  const sessions = act?.sessions || [], commits = act?.commits || [], passes = act?.passes || [];
  const openedEmails = new Set(sessions.map((s) => (s.email || "").toLowerCase()).filter(Boolean));
  const invites = act?.invites || [];
  const notOpened = invites.filter((i) => !openedEmails.has((i.email || "").toLowerCase()));
  const notes = act?.notifications || [];
  const removeCommit = async (cid) => { await shareApi.deleteCommit(share.id, cid); toast.success("Commitment removed"); load(); };

  const head = (
    <div className="ra-head">
      <span className="ra-stats">
        <span><b className="mono">{share.views || 0}</b> opens</span>
        <span><b className="mono">{act?.uniqueInvestors || 0}</b> investors</span>
        <span><b className="mono">{commits.length}</b> commitments</span>
        {passes.length > 0 && <span data-testid="ra-passes-count"><b className="mono neg">{passes.length}</b> passed</span>}
        {invites.length > 0 && <span data-testid="ra-invited"><b className="mono">{invites.length - notOpened.length}/{invites.length}</b> invited opened</span>}
      </span>
      <span style={{ display: "flex", gap: 6 }}>
        {compact && <button className="btn btn-sm" onClick={onShare} data-testid="room-activity-open">Open Deal Room</button>}
        <button className="icon-btn" onClick={load} title="Refresh" disabled={busy} data-testid="room-activity-refresh"><RefreshCw size={12} /></button>
      </span>
    </div>
  );
  if (compact) return (
    <Card title="Deal Room — Soft Equity Raise" testid="room-activity-compact">
      {head}
      <CommitBar ask={ask} committed={act?.committedTotal || 0} count={commits.length} testid="dash-commit-bar" />
    </Card>
  );
  return (
    <Card title="Deal Room — Raise & Activity" testid="room-activity">
      {head}
      <CommitBar ask={ask} committed={act?.committedTotal || 0} count={commits.length} testid="dash-commit-bar" />

      <div className="grid g2" style={{ marginTop: 14 }}>
        <div>
          <div className="section-label" style={{ marginTop: 0 }}>Soft commitments</div>
          {commits.length === 0 && <div className="bullet">No commitments yet.</div>}
          {commits.map((c) => (
            <div className="ra-row" key={c.id} data-testid={`commit-${c.id}`}>
              <div className="ra-main"><b>{c.name}</b>{c.email ? <span className="ra-sub"> · {c.email}</span> : null}{c.note ? <div className="ra-sub">“{c.note}”</div> : null}</div>
              <span className="mono pos">{m.fmt$(c.amount)}{c.className ? <span className="tag" style={{ marginLeft: 6 }} data-testid={`commit-class-${c.id}`}>Class {c.className}</span> : null}</span>
              <span className="ra-sub mono">{fmtWhen(c.createdAt)}</span>
              <button className="icon-btn" onClick={() => removeCommit(c.id)} title="Remove" data-testid={`commit-del-${c.id}`}><Trash2 size={11} /></button>
            </div>
          ))}
          {passes.length > 0 && (
            <>
              <div className="section-label">Passed — and why</div>
              {passes.map((p) => (
                <div className="ra-row" key={p.id} data-testid={`pass-${p.id}`}>
                  <div className="ra-main"><b>{p.name}</b>{p.email ? <span className="ra-sub"> · {p.email}</span> : null}
                    <div className="ra-sub">{(p.reasons || []).join(" · ")}{p.note ? ` — “${p.note}”` : ""}{p.revisit ? " · open to revisit" : ""}</div></div>
                  <span className="ra-sub mono">{fmtWhen(p.createdAt)}</span>
                </div>
              ))}
            </>
          )}
          <div className="section-label">Timeline</div>
          <Timeline items={share.timeline} testid="dash-timeline" />
          {invites.length > 0 && (
            <>
              <div className="section-label">Invited{notOpened.length ? ` · ${notOpened.length} not opened yet` : " · everyone opened"}</div>
              <div className="ra-invites" data-testid="ra-invites">
                {invites.map((i) => <span key={i.email} className={`ra-inv ${openedEmails.has(i.email) ? "opened" : ""}`} title={i.createdAt || i.invitedAt ? `Link issued ${fmtWhen(i.createdAt || i.invitedAt)}` : ""}>{i.name || i.email}</span>)}
              </div>
            </>
          )}
          {notes.length > 0 && (
            <>
              <div className="section-label">Notifications sent</div>
              {notes.slice(0, 6).map((n) => <div className="ra-sub" key={n.id} style={{ padding: "3px 0" }}>{fmtWhen(n.createdAt)} · {n.subject}{n.delivered ? "" : " (not delivered — sender config)"}</div>)}
            </>
          )}
        </div>
        <div>
          <div className="section-label" style={{ marginTop: 0 }}>Who opened the room</div>
          {sessions.length === 0 && <div className="bullet">No visits yet.</div>}
          <div className="ra-sessions">
            {sessions.slice(0, 30).map((s) => {
              const tabs = Object.entries(s.tabs || {}).sort((a, b) => b[1] - a[1]);
              const total = tabs.reduce((a, t) => a + t[1], 0);
              const dl = Object.entries(s.docs || {}).map(([did, n]) => ({ n, d: (share.docs || []).find((x) => x.id === did) })).filter((x) => x.d);
              return (
                <div className="ra-sess" key={s.id} data-testid={`session-${s.id}`}>
                  <div className="ra-sess-head">
                    <span><b>{s.name || "Anonymous"}</b>{s.email ? <span className="ra-sub"> · {s.email}</span> : null}</span>
                    <span className="ra-sub mono">{fmtWhen(s.openedAt)} · {fmtDur(total)}{s.disclaimerAcceptedAt ? " · ✓ disclaimer" : ""}</span>
                  </div>
                  {tabs.length > 0 && (
                    <div className="ra-tabs">
                      {tabs.map(([t, sec]) => (
                        <div className="ra-tab" key={t} title={fmtDur(sec)}>
                          <span className="ra-tab-name">{label(t)}</span>
                          <span className="ra-tab-track"><span className="ra-tab-fill" style={{ width: `${total ? Math.max(2, (sec / total) * 100) : 0}%` }} /></span>
                          <span className="ra-tab-dur mono">{fmtDur(sec)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {dl.length > 0 && <div className="ra-sub" style={{ marginTop: 5 }}>Downloaded: {dl.map((x) => `${x.d.label}${x.n > 1 ? ` ×${x.n}` : ""}`).join(", ")}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}
